import { Box, Paper, Typography } from "@mui/material";
import { Image as ImageIcon } from "lucide-react";
import { apiUrl } from "../../common/api/config";
import { SPACING } from "../../common/constants/spacing";
import { useAuth } from "../../common/contexts/AuthContext";
import { useTheme } from "../../common/contexts/ThemeContext";

export default function ReportPreview({ formData }) {
  const {
    title,
    logo,
    company_name,
    address,
    location,
    notes,
    include_project_summary,
    include_asset_breakdown,
    include_total_assets,
    include_total_impressions,
    include_total_royalty_revenue,
    include_total_impression_revenue,
    include_total_revenue,
    include_sales_revenue,
    include_impressions,
    include_impressions_revenue,
  } = formData;

  // Function to get logo URL
  const getLogoUrl = () => {
    if (!logo) return null;
    if (typeof logo === "string") {
      return logo.startsWith("http") ? logo : `${apiUrl}${logo}`;
    }
    return URL.createObjectURL(logo);
  };

  const logoUrl = getLogoUrl();

  const { colors } = useTheme();
  const { user } = useAuth();

  return (
    <Paper
      elevation={2}
      sx={{
        border: "1px solid",
        borderColor: colors.border.primary,
        borderRadius: 0,
        width: "595px",
        minHeight: "842px",
        bgcolor: "white",
        p: 5,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: SPACING.sm,
        }}
      >
        {/* Header Left Section */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {/* Logo */}
          <Box sx={{ flex: "0 0 auto", mb: SPACING.xs }}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Company Logo"
                style={{
                  maxWidth: "120px",
                  maxHeight: "80px",
                  objectFit: "contain",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "120px",
                  height: "80px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#f5f5f5",
                  border: "1px dashed #ccc",
                  borderRadius: 1,
                }}
              >
                <ImageIcon size={40} fill="#999" />
              </Box>
            )}
          </Box>
          <Typography variant="bodySm" sx={{ color: "black" }}>
            Report requested by: {user?.name || "User Name"}
          </Typography>
          <Typography variant="bodySm" sx={{ color: "black" }}>
            Generated at: mm/dd/yyyy
          </Typography>
          <Typography variant="bodySm" sx={{ color: "black" }}>
            Royalty Period: mm/dd/yyyy - mm/dd/yyyy
          </Typography>
        </Box>
        {/* Header Right Section - Company Info */}
        <Box>
          <Box
            sx={{
              display: "flex",
              textAlign: "right",
              ml: 2,
              flexDirection: "column",
            }}
          >
            <Typography
              variant="bodySm"
              sx={{
                color: company_name ? "black" : "#555",
              }}
            >
              {company_name || "Company Name"}
            </Typography>
            <Typography
              variant="bodySm"
              sx={{ color: address ? "black" : "#555" }}
            >
              {address || "Company Address"}
            </Typography>
            <Typography
              variant="bodySm"
              sx={{ color: location ? "black" : "#555" }}
            >
              {location || "Zip, City, Country"}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Report Title */}
      <Box sx={{ textAlign: "center", mt: 5 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            color: title ? "black" : "#777",
            fontSize: "22px",
          }}
        >
          {title || "Report Title"}
        </Typography>
      </Box>

      {/* Project Summary Section */}
      {include_project_summary && (
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "black", fontSize: "16px", mb: 2 }}
          >
            Project Summary
          </Typography>
          <Box
            sx={{
              border: "1px solid #ddd",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            {/* Table rows */}
            {include_total_assets && (
              <Box
                sx={{
                  display: "flex",
                  borderBottom: "1px solid #eee",
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <Box sx={{ flex: 1, p: 1, bgcolor: "#f9f9f9" }}>
                  <Typography
                    variant="bodySm"
                    sx={{ fontWeight: 600, color: "black" }}
                  >
                    Total Assets
                  </Typography>
                </Box>
                <Box
                  sx={{ flex: 1, p: 1, textAlign: "right", bgcolor: "white" }}
                >
                  <Box
                    sx={{
                      height: "12px",
                      width: "40px",
                      bgcolor: "#e0e0e0",
                      borderRadius: "4px",
                      ml: "auto",
                    }}
                  />
                </Box>
              </Box>
            )}
            {include_total_impressions && (
              <Box
                sx={{
                  display: "flex",
                  borderBottom: "1px solid #eee",
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <Box sx={{ flex: 1, p: 1, bgcolor: "#f9f9f9" }}>
                  <Typography
                    variant="bodySm"
                    sx={{ fontWeight: 600, color: "black" }}
                  >
                    Total Impressions
                  </Typography>
                </Box>
                <Box
                  sx={{ flex: 1, p: 1, textAlign: "right", bgcolor: "white" }}
                >
                  <Box
                    sx={{
                      height: "12px",
                      width: "60px",
                      bgcolor: "#e0e0e0",
                      borderRadius: "4px",
                      ml: "auto",
                    }}
                  />
                </Box>
              </Box>
            )}
            {include_total_royalty_revenue && (
              <Box
                sx={{
                  display: "flex",
                  borderBottom: "1px solid #eee",
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <Box sx={{ flex: 1, p: 1, bgcolor: "#f9f9f9" }}>
                  <Typography
                    variant="bodySm"
                    sx={{ fontWeight: 600, color: "black" }}
                  >
                    Total Royalty Revenue
                  </Typography>
                </Box>
                <Box
                  sx={{ flex: 1, p: 1, textAlign: "right", bgcolor: "white" }}
                >
                  <Box
                    sx={{
                      height: "12px",
                      width: "70px",
                      bgcolor: "#e0e0e0",
                      borderRadius: "4px",
                      ml: "auto",
                    }}
                  />
                </Box>
              </Box>
            )}
            {include_total_impression_revenue && (
              <Box
                sx={{
                  display: "flex",
                  borderBottom: "1px solid #eee",
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <Box sx={{ flex: 1, p: 1, bgcolor: "#f9f9f9" }}>
                  <Typography
                    variant="bodySm"
                    sx={{ fontWeight: 600, color: "black" }}
                  >
                    Total Impression Revenue
                  </Typography>
                </Box>
                <Box
                  sx={{ flex: 1, p: 1, textAlign: "right", bgcolor: "white" }}
                >
                  <Box
                    sx={{
                      height: "12px",
                      width: "70px",
                      bgcolor: "#e0e0e0",
                      borderRadius: "4px",
                      ml: "auto",
                    }}
                  />
                </Box>
              </Box>
            )}
            {include_total_revenue && (
              <Box
                sx={{
                  display: "flex",
                  borderBottom: "1px solid #eee",
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <Box sx={{ flex: 1, p: 1, bgcolor: "#f9f9f9" }}>
                  <Typography
                    variant="bodySm"
                    sx={{ fontWeight: 600, color: "black" }}
                  >
                    Total Revenue
                  </Typography>
                </Box>
                <Box
                  sx={{ flex: 1, p: 1, textAlign: "right", bgcolor: "white" }}
                >
                  <Box
                    sx={{
                      height: "12px",
                      width: "70px",
                      bgcolor: "#e0e0e0",
                      borderRadius: "4px",
                      ml: "auto",
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Asset Breakdown Section */}
      {include_asset_breakdown && (
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "black", fontSize: "16px", mb: 2 }}
          >
            Asset Breakdown
          </Typography>
          <Box
            sx={{
              border: "1px solid #ddd",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            {/* Table Header */}
            <Box
              sx={{
                display: "flex",
                bgcolor: "#f0f0f0",
                borderBottom: "1px solid #ddd",
              }}
            >
              <Box sx={{ flex: 2, p: 1 }}>
                <Typography
                  variant="bodySm"
                  sx={{ fontWeight: 700, color: "black" }}
                >
                  Title
                </Typography>
              </Box>
              {include_sales_revenue && (
                <Box sx={{ flex: 1, p: 1, textAlign: "right" }}>
                  <Typography
                    variant="bodySm"
                    sx={{ fontWeight: 700, color: "black" }}
                  >
                    Sales Revenue
                  </Typography>
                </Box>
              )}
              {include_impressions && (
                <Box sx={{ flex: 1, p: 1, textAlign: "right" }}>
                  <Typography
                    variant="bodySm"
                    sx={{ fontWeight: 700, color: "black" }}
                  >
                    Impressions
                  </Typography>
                </Box>
              )}
              {include_impressions_revenue && (
                <Box sx={{ flex: 1, p: 1, textAlign: "right" }}>
                  <Typography
                    variant="bodySm"
                    sx={{ fontWeight: 700, color: "black" }}
                  >
                    Impressions Revenue
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Skeleton Data Rows */}
            {[70, 85, 65, 75, 80].map((width, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  py: 0.75,
                  px: 1,
                  alignItems: "center",
                  borderBottom: "1px solid #eee",
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <Box sx={{ flex: 2 }}>
                  <Box
                    sx={{
                      height: "12px",
                      width: `${width}%`,
                      bgcolor: "#e0e0e0",
                      borderRadius: "4px",
                    }}
                  />
                </Box>
                {include_sales_revenue && (
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Box
                      sx={{
                        height: "12px",
                        width: "60px",
                        bgcolor: "#e0e0e0",
                        borderRadius: "4px",
                      }}
                    />
                  </Box>
                )}
                {include_impressions && (
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Box
                      sx={{
                        height: "12px",
                        width: "50px",
                        bgcolor: "#e0e0e0",
                        borderRadius: "4px",
                      }}
                    />
                  </Box>
                )}
                {include_impressions_revenue && (
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Box
                      sx={{
                        height: "12px",
                        width: "60px",
                        bgcolor: "#e0e0e0",
                        borderRadius: "4px",
                      }}
                    />
                  </Box>
                )}
              </Box>
            ))}

            {/* Total Row */}
            <Box
              sx={{
                display: "flex",
                p: 1,
                bgcolor: "#f9f9f9",
                alignItems: "center",
              }}
            >
              <Box sx={{ flex: 2 }}>
                <Typography
                  variant="bodySm"
                  sx={{ fontWeight: 700, color: "black" }}
                >
                  Total Earned Amount
                </Typography>
              </Box>
              <Box
                sx={{
                  flex:
                    include_sales_revenue ||
                    include_impressions ||
                    include_impressions_revenue
                      ? 1
                      : 0,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Box
                  sx={{
                    height: "14px",
                    width: "80px",
                    bgcolor: "#d0d0d0",
                    borderRadius: "4px",
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Footer Notes Section */}
      <Box sx={{ mt: 5 }}>
        <Typography
          variant="bodySm"
          sx={{
            color: notes ? "black" : "#777",
            whiteSpace: "pre-wrap",
          }}
        >
          {notes || "Additional notes or disclaimers can be added here."}
        </Typography>
      </Box>
    </Paper>
  );
}
